import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CaretDown, CaretUp, Plus, Trash, DownloadSimple, CheckCircle } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";

const money = (c) => `$${(c / 100).toFixed(2)}`;

const Card = ({ title, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-[#C4C8CE] bg-white mb-4">
      <button className="w-full flex items-center justify-between px-4 py-3 bg-transparent border-0 cursor-pointer"
        onClick={() => setOpen(!open)} data-testid={`growth-card-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
        <span className="font-display font-bold text-[#000000]">{title}</span>
        {open ? <CaretUp size={16} /> : <CaretDown size={16} />}
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
};

const EMPTY_PKG = { name: "", sessions: 5, price_cents: 0, expiry_enabled: false, expiry_months: 12, active: true };

export const SessionPackagesCard = () => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY_PKG);
  const load = () => api.get("/admin/packages").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.name.trim() || !form.price_cents) { toast.error("Name and price required"); return; }
    try {
      if (form.id) await api.patch(`/admin/packages/${form.id}`, form);
      else await api.post("/admin/packages", form);
      toast.success("Package saved"); setForm(EMPTY_PKG); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => { if (!window.confirm("Delete this package?")) return; await api.delete(`/admin/packages/${id}`); load(); };
  return (
    <Card title="Session Packages (packs & pricing)">
      <div className="text-xs text-[#000000] mb-3">Packs buyers purchase online. Credits are tied to their email — when they book with that email, a credit is used automatically.</div>
      {rows.map((p) => (
        <div key={p.id} className="flex items-center gap-2 border-b border-[#EDEEF0] py-2 text-sm flex-wrap" data-testid={`package-row-${p.id}`}>
          <b>{p.name}</b><span>{p.sessions} sessions · {money(p.price_cents)}</span>
          <span className="text-xs text-[#000000]">{p.expiry_enabled ? `expires ${p.expiry_months} mo` : "never expires"}{p.active ? "" : " · hidden"}</span>
          <button className="btn-secondary text-xs ml-auto" onClick={() => setForm(p)}>Edit</button>
          <button className="btn-ghost p-1 text-[#B91C1C]" onClick={() => del(p.id)} data-testid={`package-delete-${p.id}`}><Trash size={15} /></button>
        </div>
      ))}
      <div className="mt-3 grid sm:grid-cols-2 gap-2">
        <input className="input-base" placeholder="Name (e.g. 5-Pack)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pkg-name" />
        <div className="flex gap-2">
          <input className="input-base w-24" type="number" min="1" placeholder="Sessions" value={form.sessions} onChange={(e) => setForm({ ...form, sessions: parseInt(e.target.value) || 1 })} data-testid="pkg-sessions" />
          <input className="input-base flex-1" type="number" min="0" step="0.01" placeholder="Price $" value={form.price_cents ? form.price_cents / 100 : ""} onChange={(e) => setForm({ ...form, price_cents: Math.round((parseFloat(e.target.value) || 0) * 100) })} data-testid="pkg-price" />
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={form.expiry_enabled} onChange={(e) => setForm({ ...form, expiry_enabled: e.target.checked })} data-testid="pkg-expiry-toggle" />
          Credits expire after
          <input className="input-base w-16" type="number" min="1" disabled={!form.expiry_enabled} value={form.expiry_months} onChange={(e) => setForm({ ...form, expiry_months: parseInt(e.target.value) || 12 })} /> months
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Visible for purchase
        </label>
      </div>
      <div className="flex gap-2 mt-2">
        <button className="btn-primary text-sm" onClick={save} data-testid="pkg-save"><Plus size={14} /> {form.id ? "Update package" : "Add package"}</button>
        {form.id && <button className="btn-secondary text-sm" onClick={() => setForm(EMPTY_PKG)}>Cancel edit</button>}
      </div>
    </Card>
  );
};

export const CreditAccountsCard = () => {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/credit-accounts").then(({ data }) => setRows(data)).catch((e) => toast.error(formatApiError(e)));
  const adjust = async (email, delta) => {
    const note = window.prompt(`${delta > 0 ? "Add" : "Remove"} 1 credit for ${email} — reason?`, delta > 0 ? "Override — good reason" : "No-show");
    if (note === null) return;
    try { await api.post("/admin/credit-accounts/adjust", { email, delta, note }); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Card title="Package Holders — Credit Balances">
      <button className="btn-secondary text-xs mb-2" onClick={load} data-testid="credits-load">{rows === null ? "Load balances" : "Refresh"}</button>
      {rows !== null && (rows.length === 0 ? <div className="text-sm text-[#000000]">No package holders yet.</div> :
        rows.map((a) => (
          <div key={a.email} className="border-b border-[#EDEEF0] py-2 text-sm" data-testid={`credit-row-${a.email}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <b>{a.name || a.email}</b>
              <span className="text-xs text-[#000000]">{a.email}{a.phone ? ` · ${a.phone}` : ""}</span>
              <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${a.balance > 0 ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#F4F4F5] text-[#000000]"}`}>{a.balance} left</span>
              {a.expires_at && <span className="text-[11px] text-[#B45309]">expires {a.expires_at}</span>}
              <span className="ml-auto flex gap-1">
                <button className="btn-secondary text-xs" onClick={() => adjust(a.email, 1)} data-testid={`credit-add-${a.email}`}>+1</button>
                <button className="btn-secondary text-xs" onClick={() => adjust(a.email, -1)}>−1</button>
              </span>
            </div>
            <details className="text-xs text-[#000000] mt-1"><summary className="cursor-pointer">History</summary>
              {(a.history || []).slice().reverse().map((h, i) => (
                <div key={i}>{new Date(h.ts).toLocaleDateString()} · {h.delta > 0 ? `+${h.delta}` : h.delta} · {h.note}</div>
              ))}
            </details>
          </div>
        )))}
    </Card>
  );
};

const EMPTY_EVT = { title: "", date: "", time: "", location: "", description: "", mode: "fixed", price_cents: 0, capacity: 0, what_bring: "", ticket_subject: "", ticket_body: "", ticket_pdf: "", active: true };

export const EventsCard = () => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);
  const [signups, setSignups] = useState(null); // {event, rows}
  const load = () => api.get("/admin/events").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    try {
      if (form.id) await api.patch(`/admin/events/${form.id}`, form);
      else await api.post("/admin/events", form);
      toast.success("Event saved"); setForm(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => { if (!window.confirm("Delete this event and its list?")) return; await api.delete(`/admin/events/${id}`); load(); };
  const openSignups = async (e) => {
    const { data } = await api.get(`/admin/events/${e.id}/signups`);
    setSignups({ event: e, rows: data });
  };
  const toggleAttend = async (s) => {
    await api.patch(`/admin/events/signups/${s.id}`, { attended: !s.attended });
    setSignups((prev) => ({ ...prev, rows: prev.rows.map((r) => r.id === s.id ? { ...r, attended: !s.attended } : r) }));
  };
  const publicUrl = (e) => `${window.location.origin}/events/${e.slug}`;
  return (
    <Card title="Special Events — Signups, Payments & Attendance">
      {rows.map((e) => (
        <div key={e.id} className="border-b border-[#EDEEF0] py-2 text-sm" data-testid={`event-row-${e.id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <b>{e.title}</b>
            <span className="text-xs text-[#000000]">{e.date} {e.time} · {e.mode === "free" ? "Free" : e.mode === "donation" ? "Donation" : money(e.price_cents)}{e.capacity ? ` · cap ${e.capacity}` : ""}</span>
            <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-[#DBEAFE] text-[#1D4ED8]">{e.signups_count} signed up</span>
            <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-[#DCFCE7] text-[#166534]">{money(e.raised_cents)} raised</span>
            <span className="text-[11px] text-[#000000]">{e.attended_count} attended</span>
            <span className="ml-auto flex gap-1 flex-wrap">
              <a className="btn-secondary text-xs" href={publicUrl(e)} target="_blank" rel="noreferrer">Page ↗</a>
              <button className="btn-secondary text-xs" onClick={() => openSignups(e)} data-testid={`event-signups-${e.id}`}>Attendees</button>
              <button className="btn-secondary text-xs" onClick={() => setForm(e)}>Edit</button>
              <button className="btn-ghost p-1 text-[#B91C1C]" onClick={() => del(e.id)}><Trash size={15} /></button>
            </span>
          </div>
        </div>
      ))}
      {!form && <button className="btn-primary text-sm mt-3" onClick={() => setForm(EMPTY_EVT)} data-testid="event-new"><Plus size={14} /> New event</button>}
      {form && (
        <div className="mt-3 border border-[#EDEEF0] rounded-lg p-3 grid sm:grid-cols-2 gap-2">
          <input className="input-base sm:col-span-2" placeholder="Event title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-title" />
          <input className="input-base" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="event-date" />
          <input className="input-base" type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
          <input className="input-base sm:col-span-2" placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          <textarea className="input-base sm:col-span-2" rows={2} placeholder="Description (shown on the public page)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="flex gap-2 items-center">
            <select className="input-base" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} data-testid="event-mode">
              <option value="fixed">Fixed price</option>
              <option value="free">Free entry</option>
              <option value="donation">Donation (pay what you want)</option>
            </select>
            {form.mode === "fixed" && <input className="input-base w-28" type="number" min="0" step="0.01" placeholder="Price $" value={form.price_cents ? form.price_cents / 100 : ""} onChange={(e) => setForm({ ...form, price_cents: Math.round((parseFloat(e.target.value) || 0) * 100) })} data-testid="event-price" />}
          </div>
          <label className="flex items-center gap-2 text-sm">Capacity
            <input className="input-base w-24" type="number" min="0" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) || 0 })} data-testid="event-capacity" />
            <span className="text-xs text-[#000000]">(0 = unlimited)</span>
          </label>
          <textarea className="input-base sm:col-span-2" rows={2} placeholder="What to bring / what to expect (goes in the ticket email as {what_bring})" value={form.what_bring} onChange={(e) => setForm({ ...form, what_bring: e.target.value })} />
          <input className="input-base sm:col-span-2" placeholder="Ticket email subject (blank = default)" value={form.ticket_subject} onChange={(e) => setForm({ ...form, ticket_subject: e.target.value })} />
          <textarea className="input-base sm:col-span-2" rows={4} placeholder={"Ticket email body — placeholders: {name} {event} {date} {time} {location} {what_bring} (blank = default)"} value={form.ticket_body} onChange={(e) => setForm({ ...form, ticket_body: e.target.value })} data-testid="event-ticket-body" />
          <input className="input-base sm:col-span-2" placeholder="Ticket PDF attachment path (paste a /api/media/… URL from an upload, optional)" value={form.ticket_pdf} onChange={(e) => setForm({ ...form, ticket_pdf: e.target.value })} />
          <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Live (page reachable)</label>
          <div className="flex gap-2 sm:col-span-2">
            <button className="btn-primary text-sm" onClick={save} data-testid="event-save">{form.id ? "Update event" : "Create event"}</button>
            <button className="btn-secondary text-sm" onClick={() => setForm(null)}>Cancel</button>
          </div>
        </div>
      )}
      {signups && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setSignups(null)} data-testid="event-signups-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="font-bold mb-1">{signups.event.title} — attendees</div>
            <div className="text-xs text-[#000000] mb-3">{signups.rows.length} signed up · tap the circle on event day to check people in</div>
            {signups.rows.map((s) => (
              <div key={s.id} className="flex items-center gap-2 border-b border-[#EDEEF0] py-2 text-sm" data-testid={`signup-row-${s.id}`}>
                <button className="bg-transparent border-0 cursor-pointer p-0" onClick={() => toggleAttend(s)} title="Toggle attended" data-testid={`attend-toggle-${s.id}`}>
                  <CheckCircle size={22} weight={s.attended ? "fill" : "regular"} color={s.attended ? "#16A34A" : "#A1A1AA"} />
                </button>
                <div className="flex-1">
                  <b>{s.name}</b> <span className="text-xs text-[#000000]">{s.email}{s.phone ? ` · ${s.phone}` : ""}</span>
                </div>
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${s.status === "paid" ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#F4F4F5] text-[#000000]"}`}>
                  {s.status === "paid" ? money(s.amount_cents) : "Free"}
                </span>
              </div>
            ))}
            <div className="flex justify-end mt-3"><button className="btn-secondary" onClick={() => setSignups(null)}>Close</button></div>
          </div>
        </div>
      )}
    </Card>
  );
};

export const CourseBroadcastCard = () => {
  const [data, setData] = useState(null);
  const [subject, setSubject] = useState("Course updates — what's new");
  const [intro, setIntro] = useState("Here's a summary of everything new since the last update:");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/course-changelog").then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const toggle = async (enabled) => {
    await api.post("/admin/course-changelog/toggle", { enabled });
    setData({ ...data, staff_lesson_emails_enabled: enabled });
    toast.success(enabled ? "Instant staff emails ON" : "Instant staff emails OFF — changes collect below");
  };
  const removeEntry = async (id) => {
    await api.delete(`/admin/course-changelog/${id}`);
    setData({ ...data, pending: data.pending.filter((p) => p.id !== id) });
  };
  const send = async () => {
    if (!window.confirm(`Send this summary of ${data.pending.length} change(s) to ${data.recipient_count} teacher(s)/board admin(s)?`)) return;
    setBusy(true);
    try {
      const { data: res } = await api.post("/admin/course-changelog/broadcast", { subject, intro });
      toast.success(`Broadcast sent to ${res.recipients} people (${res.changes} changes)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Card title="Course Update Emails — Staff & School Broadcast">
      <label className="flex items-center gap-2 text-sm cursor-pointer mb-2">
        <input type="checkbox" checked={data.staff_lesson_emails_enabled} onChange={(e) => toggle(e.target.checked)} data-testid="staff-emails-toggle" />
        Email staff instantly on every lesson change <span className="text-xs text-[#000000]">(off = quiet; changes collect in the list below)</span>
      </label>
      <div className="text-xs text-[#000000] mb-3">
        Public students always get in-app updates automatically. Teachers & board admins only hear from you when you send the summary below.
        {data.last_broadcast_at && <> Last broadcast: {new Date(data.last_broadcast_at).toLocaleDateString()}.</>}
      </div>
      <div className="text-sm font-bold text-[#000000] mb-1">Changes since last broadcast ({data.pending.length})</div>
      {data.pending.length === 0 ? (
        <div className="text-sm text-[#000000]" data-testid="changelog-empty">Nothing new yet — lesson additions and edits will appear here.</div>
      ) : (
        <>
          {data.pending.map((p) => (
            <div key={p.id} className="flex items-center gap-2 border-b border-[#EDEEF0] py-1.5 text-sm" data-testid={`changelog-row-${p.id}`}>
              <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${p.action === "added" ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#DBEAFE] text-[#1D4ED8]"}`}>{p.action}</span>
              <b>{p.lesson_title}</b>
              <span className="text-xs text-[#000000]">{(p.courses || []).join(", ")} · {p.ts?.slice(0, 10)}</span>
              <button className="btn-ghost p-1 text-[#B91C1C] ml-auto" title="Remove from this broadcast" onClick={() => removeEntry(p.id)} data-testid={`changelog-remove-${p.id}`}><Trash size={14} /></button>
            </div>
          ))}
          <div className="mt-3 grid gap-2">
            <input className="input-base" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="broadcast-subject" />
            <textarea className="input-base" rows={2} value={intro} onChange={(e) => setIntro(e.target.value)} data-testid="broadcast-intro" />
            <button className="btn-primary text-sm" onClick={send} disabled={busy} data-testid="broadcast-send">
              {busy ? "Sending…" : `Send summary to ${data.recipient_count} teacher(s) & board admin(s)`}
            </button>
          </div>
        </>
      )}
    </Card>
  );
};

export const QrCodesCard = () => {
  const [events, setEvents] = useState([]);
  const [target, setTarget] = useState("");
  const [custom, setCustom] = useState("");
  const [label, setLabel] = useState("");
  useEffect(() => { api.get("/admin/events").then(({ data }) => setEvents(data)).catch(() => {}); }, []);
  const origin = window.location.origin;
  const url = target === "custom" ? custom : target;
  const download = async () => {
    if (!url) { toast.error("Pick a target first"); return; }
    try {
      const { data } = await api.get("/admin/qr", { params: { data: url, label }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(data);
      a.download = "qr-code.png";
      a.click();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Card title="QR Codes — for posters & flyers">
      <div className="grid sm:grid-cols-2 gap-2">
        <select className="input-base" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="qr-target"
          onFocus={() => api.get("/admin/events").then(({ data }) => setEvents(data)).catch(() => {})}>
          <option value="">Choose what the QR points to…</option>
          <option value={`${origin}/?src=qr`}>Website home / booking</option>
          <option value={`${origin}/?book=1&src=qr`}>Booking dialog (opens directly)</option>
          {events.map((e) => <option key={e.id} value={`${origin}/events/${e.slug}?src=qr`}>Event: {e.title}</option>)}
          <option value="custom">Custom URL…</option>
        </select>
        {target === "custom" && <input className="input-base" placeholder="https://…" value={custom} onChange={(e) => setCustom(e.target.value)} />}
        <input className="input-base" placeholder="Label under the code (optional, e.g. SCAN TO BOOK)" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="qr-label" />
      </div>
      <button className="btn-primary text-sm mt-2" onClick={download} data-testid="qr-download"><DownloadSimple size={14} /> Download poster-ready PNG</button>
    </Card>
  );
};
